import React, { useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import "../styles/company/about.css"
import logo from "../images/geointelli-q.png"

// Icons
import { 
  FaLayerGroup, FaWater, FaOilCan, FaSatellite, 
  FaMountain, FaTemperatureHigh, FaChartLine, 
  FaMapMarkedAlt, FaRobot, FaBrain, FaDatabase
} from 'react-icons/fa';

const FeatureCard = ({ icon, title, description, delay }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { 
            duration: 0.6, 
            delay: delay * 0.2 
          }
        }
      }}
      className="mb-4"
    >
      <Card className="h-100 feature-card ">
        <Card.Body>
          <div className="feature-icon">{icon}</div>
          <Card.Title className="mt-3">{title}</Card.Title>
          <Card.Text>{description}</Card.Text>
        </Card.Body>
      </Card>
    </motion.div>
  );
};

const TechBadge = ({ text, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: delay * 0.1 }}
      className="d-inline-block m-1"
    >
      <Badge bg="primary" className="tech-badge">{text}</Badge>
    </motion.div>
  );
};

const AboutPage = () => {
  const heroControls = useAnimation();
  const [ heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (heroInView) {
      heroControls.start('visible');
    }
  }, [heroControls, heroInView]);

  // Sample data - in a real app, you would fetch this or pass it as props
  // const data = [
  //   {"cohort":"Jan","month0":100,"month1":88.8,"month2":79.5,"month3":74.2,"month4":68.2,"month5":65.4,"month6":59.4,"totalUsers":2854},
  //   {"cohort":"Feb","month0":100,"month1":89.2,"month2":80.6,"month3":72.1,"month4":65.3,"month5":62.3,"month6":55.7,"totalUsers":2960}
  // ];

  return (
    <div className="about-page">
      {/* Main Content */}
      <Container className="py-5">
        {/* About Section */}
        <Row className="mb-5">
  <Col lg={6}>
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8 }}
    >
      <h2 className="section-title">About GeoIntelli-Q</h2>
      <p className="section-description">
        GeoIntelli-Q is a cutting-edge geoscience analytics platform that revolutionizes how geoscientists, 
        petroleum engineers, hydrologists, and environmental researchers interact with geospatial data. 
        By combining advanced signal processing, machine learning, and artificial intelligence, our software 
        provides unprecedented insights into Earth's systems and resources.
      </p>
      <p className="section-description">
        Developed by a team of experts in geophysics, geology, data science, and software engineering, 
        GeoIntelli-Q bridges the gap between traditional geoscience methodologies and modern computational techniques, 
        enabling more accurate predictions, interpretations, and resource management decisions.
      </p>
    </motion.div>
  </Col>
  <Col lg={6}>
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8 }}
      className="tech-image-container  mx-auto"
    > 
      <div className="tech-image p-3 mx-auto ">
        <img className='img-fluid p-md-5' src={logo} alt="geo_image"/>
        {/* <img className='img-fluid  w-75 h-auto p-md-5' src="https://myacademyimages.s3.eu-north-1.amazonaws.com/cuk/olewin_bmh.png" alt="company"/> */}
       <h6 className='text-center'>Designed & Developed by Olewin  </h6> 
      </div>
     
    </motion.div>
  </Col>
</Row>

        {/* Disciplines Covered */}
        <Row className="mb-5">
          <Col xs={12}>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="section-title text-center mb-5"
            >
              Disciplines Covered
            </motion.h2>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <FeatureCard
              icon={<FaLayerGroup />}
              title="Geology & Stratigraphy"
              description="Detailed analysis of geological formations, stratigraphy, and structural geology with 3D visualization and modeling capabilities."
              delay={0}
            />
          </Col>
          <Col md={4}>
            <FeatureCard
              icon={<FaChartLine />}
              title="Geophysics"
              description="Comprehensive tools for seismic, gravity, magnetic, electrical, and electromagnetic survey data processing and interpretation."
              delay={1}
            />
          </Col>
          <Col md={4}>
            <FeatureCard
              icon={<FaSatellite />}
              title="Remote Sensing"
              description="Advanced satellite image analysis with multispectral and hyperspectral capabilities for surface mapping and change detection."
              delay={2}
            />
          </Col>
          <Col md={4}>
            <FeatureCard
              icon={<FaOilCan />}
              title="Petroleum Engineering"
              description="Well logging analysis, reservoir characterization, production optimization, and field development planning tools."
              delay={3}
            />
          </Col>
          <Col md={4}>
            <FeatureCard
              icon={<FaWater />}
              title="Hydrology & Hydrogeology"
              description="Groundwater modeling, watershed analysis, and water resource management with predictive capabilities."
              delay={4}
            />
          </Col>
          <Col md={4}>
            <FeatureCard
              icon={<FaTemperatureHigh />}
              title="Meteorology & Climatology"
              description="Climate data analysis, weather pattern recognition, and long-term climate trend forecasting."
              delay={5}
            />
          </Col>
        </Row>

        {/* Capabilities Section */}
        <Row className="mt-5 pt-5">
          <Col xs={12}>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="section-title text-center mb-5"
            >
              Key Capabilities
            </motion.h2>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="capability-item">
                <h3><FaMapMarkedAlt /> Geospatial Analysis</h3>
                <ul>
                  <li>Multi-layered GIS integration with vector and raster data support</li>
                  <li>Advanced spatial statistics and geostatistical modeling</li>
                  <li>Terrain analysis and 3D geological modeling</li>
                  <li>Satellite imagery processing with atmospheric correction</li>
                </ul>
              </div>

              <div className="capability-item">
                <h3><FaChartLine /> Signal Processing</h3>
                <ul>
                  <li>Seismic data processing with advanced noise reduction algorithms</li>
                  <li>Resistivity and electromagnetic data inversion techniques</li>
                  <li>Gravity and magnetic field analysis and modeling</li>
                  <li>Well log signal enhancement and interpretation</li>
                </ul>
              </div>
            </motion.div>
          </Col>
          <Col md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="capability-item">
                <h3><FaBrain /> AI & Machine Learning</h3>
                <ul>
                  <li>Predictive modeling for resource estimation and risk assessment</li>
                  <li>Pattern recognition for geological feature identification</li>
                  <li>Time series forecasting for hydrological and climate data</li>
                  <li>Automated anomaly detection in geophysical datasets</li>
                </ul>
              </div>

              <div className="capability-item">
                <h3><FaDatabase /> Data Integration</h3>
                <ul>
                  <li>Seamless integration of multi-source geoscience data</li>
                  <li>Standardized data formats and conversion utilities</li>
                  <li>Cloud-based collaboration and data sharing</li>
                  <li>Real-time data streaming and processing capabilities</li>
                </ul>
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Applications Section */}
        <Row className="mt-5 pt-5">
          <Col xs={12}>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="section-title text-center mb-5"
            >
              Applications
            </motion.h2>
          </Col>
        </Row>

        <Row className="justify-content-center">
          <Col lg={10}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="applications-grid"
            >
              <div className="application-card">
                <div className="application-icon"><FaOilCan /></div>
                <h4>Oil & Gas Exploration</h4>
                <p>Integrated workflows for prospect identification, reservoir characterization, and production optimization.</p>
              </div>
              <div className="application-card">
                <div className="application-icon"><FaMountain /></div>
                <h4>Mineral Exploration</h4>
                <p>Advanced targeting tools for mineral deposits using multi-parameter anomaly detection and prediction.</p>
              </div>
              <div className="application-card">
                <div className="application-icon"><FaWater /></div>
                <h4>Water Resource Management</h4>
                <p>Comprehensive groundwater modeling and watershed analysis for sustainable water management.</p>
              </div>
              <div className="application-card">
                <div className="application-icon"><FaTemperatureHigh /></div>
                <h4>Climate Research</h4>
                <p>Data-driven climate analysis and prediction tools for environmental research and planning.</p>
              </div>
              <div className="application-card">
                <div className="application-icon"><FaLayerGroup /></div>
                <h4>Environmental Monitoring</h4>
                <p>Change detection and impact assessment tools for environmental monitoring and protection.</p>
              </div>
              <div className="application-card">
                <div className="application-icon"><FaRobot /></div>
                <h4>Research & Education</h4>
                <p>Advanced analysis tools and visualization capabilities for geoscience research and education.</p>
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Technology Stack */}
        <Row className="mt-5 pt-5">
          <Col xs={12}>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="section-title text-center"
            >
              Technology Stack
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-4"
            >
              Built with cutting-edge technologies for performance, scalability, and accuracy
            </motion.p>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col lg={10} className="text-center">
            <div className="tech-badges">
              <TechBadge text="Python" delay={0} />
              <TechBadge text="TensorFlow" delay={1} />
              <TechBadge text="PyTorch" delay={2} />
              <TechBadge text="CUDA" delay={3} />
              <TechBadge text="NumPy" delay={4} />
              <TechBadge text="SciPy" delay={5} />
              <TechBadge text="GDAL" delay={6} />
              <TechBadge text="ArcGIS API" delay={7} />
              <TechBadge text="OpenCV" delay={8} />
              <TechBadge text="Scikit-learn" delay={9} />
              <TechBadge text="Pandas" delay={10} />
              <TechBadge text="Dask" delay={11} />
              <TechBadge text="QGIS API" delay={12} />
              <TechBadge text="SeismicUnix" delay={13} />
              <TechBadge text="OpenDTect" delay={14} />
              <TechBadge text="Spatial Algorithms" delay={15} />
              <TechBadge text="Signal Processing" delay={16} />
              <TechBadge text="Cloud Computing" delay={17} />
              <TechBadge text="Distributed Computing" delay={18} />
              <TechBadge text="Real-time Analytics" delay={19} />
            </div>
          </Col>
        </Row>

        {/* Call to Action */}
        <Row className="mt-5">
          <Col xs={12} className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="cta-section"
            >
              <h2>Ready to Transform Your Geoscience Workflow?</h2>
              <p>Experience the power of GeoIntelli-Q with a personalized demo tailored to your specific needs.</p>
              <Button variant="primary" size="lg" className="mt-3 cta-button">Contact us</Button> <a href="https://olewingroup.com/contact" target='_blank' rel='noreferrer' >Click Here</a>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AboutPage;